/**
 * Live draft preview (DAB-861): the pure half of the editor's preview pane.
 *
 * The pane is a state machine, so every rule the editor depends on — when to
 * mint, when to give up and offer the build link, when a save may reload the
 * frame, which messages to believe — is testable without rendering anything.
 *
 *   idle ─open─▶ minting ─minted─▶ loading ─cms:ready─▶ ready
 *                 ▲   │               │  ▲                │
 *                 │   │ mint failed   │  └ warming (timer restarts)
 *                 │   ▼               ▼ 6 s / busy / misconfigured
 *                 │ fallback ◀────────┘
 *                 └── remint (near expiry, session-* notice, page/lang change)
 *
 * A mint answered with 503 `preview_unavailable` does not fall back: it marks
 * the environment unavailable and the editor hides the feature altogether.
 *
 * Message contract (hpn-landing `src/preview/bridge.ts`):
 *   preview -> admin  {type: "cms:ready", path, degraded}
 *                     {type: "cms:unavailable", reason}
 *                     {type: "cms:focus", path}   click-to-edit (DAB-902)
 *   admin -> preview  {type: "cms:reload"}
 */

/** Neither `cms:ready` nor `cms:unavailable` within this long: offer the build link. */
export const READY_TIMEOUT_MS = 6000;

/** Re-mint this long before the session token expires (the design doc's "about 5 min"). */
export const REMINT_BEFORE_MS = 5 * 60 * 1000;

/** A mint request still unanswered after this long: offer the build link. */
export const MINT_TIMEOUT_MS = 10000;

export const PREVIEW_UNAVAILABLE = "preview_unavailable";

/**
 * The only session refusals a new token can cure (hpn-landing
 * `middleware.ts`): the cookie is missing or its token expired. Every other
 * `session-*` code (signature, env, audience, lifetime, malformed) would be
 * refused again for a fresh token too, so re-minting would just loop into 429.
 */
const RENEWABLE_SESSION_REASONS = ["session-required", "session-expired"];

export const initialLivePreviewState = {
  /** null until the availability probe answers; false hides the toggle. */
  available: null,
  status: "idle",
  url: null,
  expiresAt: null,
  /** Bumped by every request for a session; the mint effect keys on it. */
  mintNonce: 0,
  /** Bumped by every minted session and every `warming` notice; keys the ready timer. */
  attempt: 0,
  reason: null,
  degraded: [],
  /**
   * Re-mints spent on renewable session notices since the frame last reached
   * `ready` (or since open/retry). The budget is one: a fresh session refused
   * again before ever rendering will not be cured by a third.
   */
  sessionRemints: 0,
};

export const isOpen = (state) => state.status !== "idle";

const requestMint = (state, reason = null) => ({
  ...state,
  status: "minting",
  reason,
  mintNonce: state.mintNonce + 1,
});

export function livePreviewReducer(state, action) {
  switch (action.type) {
    case "availability":
      return { ...state, available: Boolean(action.available) };

    case "open":
      if (state.available !== true || state.status !== "idle") return state;
      return requestMint(state);

    case "retry":
      return state.status === "fallback" ? requestMint({ ...state, sessionRemints: 0 }) : state;

    case "close":
      return { ...initialLivePreviewState, available: state.available, mintNonce: state.mintNonce };

    // The session is about to expire, or the page/language changed: a new
    // session, keeping the pane open. The old frame stays until the new URL.
    case "remint":
      return isOpen(state) ? requestMint(state, action.reason || null) : state;

    case "minted":
      if (state.status !== "minting" || action.nonce !== state.mintNonce) return state;
      return {
        ...state,
        status: "loading",
        url: action.url,
        expiresAt: action.expiresAt,
        attempt: state.attempt + 1,
        reason: null,
        degraded: [],
      };

    case "mint-failed":
      if (state.status !== "minting" || action.nonce !== state.mintNonce) return state;
      if (action.code === PREVIEW_UNAVAILABLE) {
        return { ...initialLivePreviewState, available: false, mintNonce: state.mintNonce };
      }
      return { ...state, status: "fallback", reason: action.code || "mint-failed" };

    // The mint request hung; its late answer is dropped by the `minted` guard.
    case "mint-timeout":
      if (state.status !== "minting" || action.nonce !== state.mintNonce) return state;
      return { ...state, status: "fallback", reason: "mint-timeout" };

    case "timeout":
      // Only the attempt that armed the timer may end it.
      if (state.status !== "loading" || action.attempt !== state.attempt) return state;
      return { ...state, status: "fallback", reason: "timeout" };

    case "message":
      return onPreviewMessage(state, action.data);

    default:
      return state;
  }
}

function onPreviewMessage(state, data) {
  if (state.status !== "loading" && state.status !== "ready") return state;
  if (data.type === "cms:ready") {
    return {
      ...state,
      status: "ready",
      reason: null,
      degraded: Array.isArray(data.degraded) ? data.degraded.map(String) : [],
      sessionRemints: 0,
    };
  }
  if (data.type !== "cms:unavailable") return state;

  const reason = typeof data.reason === "string" ? data.reason : "unknown";
  // The notice page refreshes itself; give it a fresh 6 s.
  if (reason === "warming" || reason === "rate-limited") {
    return { ...state, status: "loading", reason, attempt: state.attempt + 1 };
  }
  // A missing or expired session: one new session, not a fallback. Any other
  // refusal, or a second one before the frame ever rendered, is final.
  if (RENEWABLE_SESSION_REASONS.includes(reason) && state.sessionRemints < 1) {
    return requestMint({ ...state, sessionRemints: state.sessionRemints + 1 }, reason);
  }
  // The frame explains which field broke; showing it is the useful answer.
  if (reason === "render-error") return { ...state, status: "ready", reason };
  return { ...state, status: "fallback", reason };
}

const PREVIEW_MESSAGE_TYPES = ["cms:ready", "cms:unavailable", "cms:focus"];

/**
 * The only messages the editor acts on: from the configured preview origin,
 * from this editor's own frame, shaped like the contract.
 */
export const isTrustedPreviewMessage = (event, { origin, frameWindow }) =>
  Boolean(origin) &&
  Boolean(frameWindow) &&
  event?.origin === origin &&
  event.source === frameWindow &&
  typeof event.data === "object" &&
  event.data !== null &&
  PREVIEW_MESSAGE_TYPES.includes(event.data.type);

/**
 * The field path a trusted `cms:focus` message asks for, or null. A path is
 * dotted segments of letters, digits, `_` and `-` (`hero.title`,
 * `howItWorks.steps.n-1a2b3c4d.title`); anything else is not one of ours.
 */
export const focusPathFrom = (data) =>
  data?.type === "cms:focus" &&
  typeof data.path === "string" &&
  data.path.length <= 300 &&
  /^[\w-]+(\.[\w-]+)*$/.test(data.path)
    ? data.path
    : null;

/**
 * Which editor input a preview path names (DAB-902), or null.
 *
 * A plain field's path is its manifest path. A `list` item's part is
 * `<list path>.<item id>.<part key>`, as `ListInput` names its part fields.
 * Locked fields are code-owned: there is nothing to edit, so nothing to focus.
 */
export const focusTargetFor = (fields, path) => {
  if (!Array.isArray(fields) || typeof path !== "string") return null;
  const field = fields.find((item) => item.path === path && item.kind !== "list");
  if (field) return field.locked ? null : { fieldPath: field.path };

  const list = fields.find((item) => item.kind === "list" && path.startsWith(`${item.path}.`));
  if (!list) return null;
  const rest = path.slice(list.path.length + 1);
  const dot = rest.lastIndexOf(".");
  if (dot <= 0) return null;
  const key = rest.slice(dot + 1);
  const part = (list.itemFields || []).find((item) => item.key === key);
  if (!part || part.locked) return null;
  return { fieldPath: list.path, itemId: rest.slice(0, dot), key };
};

/** A minted URL must be the session exchange on the configured preview origin. */
export const isPreviewSessionUrl = (url, origin) => {
  try {
    const parsed = new URL(url);
    return parsed.origin === origin && parsed.pathname === "/__preview/session";
  } catch {
    return false;
  }
};

/** How long until the session should be re-minted (0 = now). */
export const remintDelayMs = (expiresAt, nowMs = Date.now()) => {
  const expiry = Date.parse(expiresAt);
  if (Number.isNaN(expiry)) return 0;
  return Math.max(0, expiry - REMINT_BEFORE_MS - nowMs);
};

/**
 * What a successful draft save does to the pane. A near-expiry session is
 * re-minted instead (the new session renders the saved draft anyway); a frame
 * that is still loading renders the saved draft when it gets there.
 */
export const afterSaveAction = (state, nowMs = Date.now()) => {
  if (state.status !== "ready") return "none";
  return remintDelayMs(state.expiresAt, nowMs) === 0 ? "remint" : "reload";
};

/** Why the fallback card is showing, in words; the code is shown beside it. */
export const fallbackReasonText = (reason) => {
  if (reason === "mint-timeout") return "Сървърът не отговори навреме при създаването на сесия за преглед.";
  if (reason === "timeout") return "Прегледът не отговори в рамките на няколко секунди.";
  if (RENEWABLE_SESSION_REASONS.includes(reason)) {
    return "Сесията за преглед изтече и не можа да бъде подновена.";
  }
  if (typeof reason === "string" && reason.startsWith("session-")) {
    return "Сървърът за преглед отказа сесията — връзката е невалидна или е за друга среда.";
  }
  if (reason === "origin-mismatch") return "Сесията за преглед сочи към неочакван адрес.";
  if (reason === "busy") return "Сървърът за преглед е натоварен.";
  return "Прегледът не можа да се отвори.";
};

/** The API's refusal code, if the error carries one. */
export const mintErrorCode = (error) =>
  error?.response?.data?.code ||
  (error?.response?.status ? `http-${error.response.status}` : "network");
